import { useEffect, useState } from "react";
import { KaggleBioGuide } from "../components/KaggleBioGuide";

type VerificationParticipantType =
  | "regular"
  | "university"
  | "graduate"
  | "professional"
  | "exhibition";

type VerificationSession = {
  verification_id: number;
  verification_text: string;
  kaggle_id: string;
  participant_type?: VerificationParticipantType;
  expires_at?: string;
};

type VerificationResponse = {
  status?: string;
  message?: string;
  registration_id?: number;
  participant_type?: VerificationParticipantType;
  registration_mode?: "created" | "updated_existing";
  score_update?: {
    status?: "ok" | "pending";
    saved_competitions?: number;
    eligible_competitions?: number;
    message?: string;
  };
};

type VerificationState =
  | "loading"
  | "ready"
  | "checking"
  | "error"
  | "success"
  | "missing";

export function LeagueVerificationPage() {
  const [verification, setVerification] = useState<VerificationSession | null>(
    null,
  );
  const [state, setState] = useState<VerificationState>("loading");
  const [message, setMessage] = useState("");
  const [completedParticipantType, setCompletedParticipantType] = useState<
    VerificationParticipantType | undefined
  >();
  const [registrationMode, setRegistrationMode] = useState<
    "created" | "updated_existing"
  >("created");

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("aiyss-league-verification");
      if (!stored) {
        setState("missing");
        return;
      }

      const parsed = JSON.parse(stored) as Partial<VerificationSession>;
      if (
        typeof parsed.verification_id !== "number" ||
        typeof parsed.verification_text !== "string" ||
        typeof parsed.kaggle_id !== "string"
      ) {
        setState("missing");
        return;
      }

      setVerification(parsed as VerificationSession);
      setState("ready");
    } catch {
      setState("missing");
    }
  }, []);

  const handleVerify = async () => {
    if (!verification) return;

    setState("checking");
    setMessage("KaggleのBioを確認しています…");

    try {
      const response = await fetch("/api/league_verify.php", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          verification_id: verification.verification_id,
        }),
      });
      const result = (await response
        .json()
        .catch(() => ({}))) as VerificationResponse;

      if (!response.ok || result.status !== "ok") {
        throw new Error(result.message || "Bioの確認に失敗しました。");
      }

      sessionStorage.removeItem("aiyss-league-verification");
      setCompletedParticipantType(
        result.participant_type ?? verification.participant_type,
      );
      setRegistrationMode(result.registration_mode ?? "created");
      setState("success");
      const scoreMessage = result.score_update
        ? result.score_update.status === "ok"
          ? `登録したユーザーのKaggle結果を${
              result.score_update.saved_competitions ?? 0
            }件取得しました。`
          : result.score_update.message ||
            "Kaggle結果は次回の定期更新で再取得します。"
        : "";
      setMessage(
        [result.message || "リーグへの登録が完了しました。", scoreMessage]
          .filter(Boolean)
          .join(" "),
      );
    } catch (error) {
      setState("error");
      setMessage(
        error instanceof Error
          ? error.message
          : "Bioの確認に失敗しました。時間をおいてお試しください。",
      );
    }
  };

  return (
    <main id="top" className="register-page">
      <section className="register-hero" aria-labelledby="verification-title">
        <div className="register-shell">
          <p className="league-kicker">
            AIYSS / AI COMPETITION LEAGUE / VERIFICATION
          </p>
          <h1 id="verification-title">Kaggle Bioを確認する。</h1>
          <p>発行された認証キーをKaggleプロフィールのBioへ追加してください。</p>
        </div>
      </section>

      <section
        className="register-content"
        aria-labelledby="verification-card-title"
      >
        <div className="register-shell">
          <div className="register-heading">
            <p className="league-eyebrow">STEP 02 / BIO VERIFICATION</p>
            <h2 id="verification-card-title">
              認証キーをBioへ追加してください。
            </h2>
            <p>
              Kaggleプロフィールを更新したあと、下の確認ボタンを押してください。認証キーの有効期限は発行から24時間です。
            </p>
          </div>

          {state === "loading" && (
            <p className="register-status">認証情報を読み込んでいます…</p>
          )}

          {state === "missing" && (
            <div className="register-verification-card">
              <h3>認証情報が見つかりません。</h3>
              <p>登録情報の入力からやり直してください。</p>
              <a
                className="league-primary-link register-button"
                href="/league/register/"
              >
                登録ページへ戻る <span aria-hidden="true">→</span>
              </a>
            </div>
          )}

          {verification &&
            state !== "missing" &&
            state !== "loading" &&
            state !== "success" && (
              <div className="register-verification-card">
                <div className="register-verification-target">
                  <span>KAGGLE ACCOUNT</span>
                  <strong>{verification.kaggle_id}</strong>
                </div>
                <p className="register-instruction">
                  Kaggleのプロフィール編集画面を開き、Bio欄に次の文字列をそのまま追加してください。
                </p>
                <div
                  className="register-verification-code"
                  aria-label="Kaggle Bioに追加する認証キー"
                >
                  {verification.verification_text}
                </div>
                <KaggleBioGuide
                  profileUrl={`https://www.kaggle.com/${encodeURIComponent(
                    verification.kaggle_id,
                  )}`}
                  onVerify={handleVerify}
                  isChecking={state === "checking"}
                  message={message}
                />
              </div>
            )}

          {state === "success" && (
            <div
              className="register-verification-card is-success"
              role="status"
            >
              <span className="register-success-mark" aria-hidden="true">
                ✓
              </span>
              <h3>
                {(completedParticipantType ??
                  verification?.participant_type) === "professional"
                  ? "社会人カテゴリーへの登録が完了しました。"
                  : (completedParticipantType ??
                      verification?.participant_type) === "graduate" ||
                    (completedParticipantType ??
                      verification?.participant_type) === "exhibition"
                  ? "大学院生カテゴリーへの登録が完了しました。"
                  : (completedParticipantType ??
                      verification?.participant_type) === "university"
                  ? "大学生部門への登録が完了しました。"
                  : "リーグへの登録が完了しました。"}
              </h3>
              {registrationMode === "updated_existing" && (
                <p className="register-update-notice">
                  このKaggle
                  IDはすでに登録されていたため、最初の登録フォームで入力した設定を既存ユーザーに上書きしました。
                </p>
              )}
              <p>
                {message}
                <br />
                Kaggle
                Bioに追加した認証トークンは、登録完了後に削除して問題ありません。
              </p>
              <a
                className="league-primary-link register-button"
                href="/league/"
              >
                年間リーグを見る <span aria-hidden="true">→</span>
              </a>
            </div>
          )}

          <a className="register-back-link" href="/league/">
            ← 年間リーグの説明へ戻る
          </a>
        </div>
      </section>
    </main>
  );
}
